import React, { useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { AlertCircle, RotateCcw, X, Download } from "lucide-react";
import { GenericTable } from "@shared/components/GenericTable";
import {
  TableColumn,
  TableAction,
  EnhancedTableAction,
} from "@shared/types/table";
import { Badge } from "@shared/ui/badge";
import { Switch } from "@shared/ui/switch";
import { Checkbox } from "@shared/ui/checkbox";
import { toast } from "@shared/hooks/use-toast";
import { Button } from "@shared/ui/button";
import { PAGE_SIZE } from "@shared/utils/constants";
import { useGetConnectionErrors } from "@features/ix/smartMeterOem/hooks";

interface FailedRecord {
  id: string;
  record: string;
  flow: string;
  error: string;
  time: string;
  attempts: string;
}

export const DiagnosticsTab: React.FC = () => {
  const { id } = useParams();
  const [page, setPage] = useState(1);

  const { data: connectionErrorsData } = useGetConnectionErrors(id || "", {
    page,
    limit: PAGE_SIZE,
  });

  const failedRecordsData = useMemo<FailedRecord[]>(() => {
    const results = connectionErrorsData?.results || [];
    return results.map((item: any) => ({
      id: item.id?.toString(),
      record: item.recordId || item.record_id || item.record || "-",
      flow: item.flow || "-",
      error: item.errorMessage || item.error_message || item.error || "-",
      time: item.timestamp || item.time || "-",
      attempts:
        item.attempts ||
        item.retry_attempts ||
        item.retryAttempts ||
        "0 retries",
    }));
  }, [connectionErrorsData]);

  const totalItems = connectionErrorsData?.count || 0;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE) || 1;

  const pagination = {
    currentPage: page,
    totalPages,
    totalItems,
    itemsPerPage: PAGE_SIZE,
    startItem: totalItems === 0 ? 0 : (page - 1) * PAGE_SIZE + 1,
    endItem: Math.min(page * PAGE_SIZE, totalItems),
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const columns = useMemo<TableColumn<FailedRecord>[]>(
    () => [
      {
        header: "Record",
        key: "record",
        render: (_val, row) => (
          <span className="font-extrabold text-gray-900">{row.record}</span>
        ),
      },
      {
        header: "Flow",
        key: "flow",
        render: (_val, row) => (
          <Badge
            variant="outline"
            className="text-[10px] font-bold border border-gray-200 text-gray-500 bg-white rounded px-2.5 py-0.5"
          >
            {row.flow}
          </Badge>
        ),
      },
      {
        header: "Error",
        key: "error",
        render: (_val, row) => (
          <span className="text-red-650 font-semibold text-xs leading-relaxed">
            {row.error}
          </span>
        ),
      },
      {
        header: "Time",
        key: "time",
        render: (_val, row) => (
          <span className="text-gray-500 font-semibold">{row.time}</span>
        ),
      },
      {
        header: "Attempts",
        key: "attempts",
        render: (_val, row) => (
          <Badge
            variant="outline"
            className="bg-blue-50 text-blue-700 border border-blue-200 font-semibold text-[10px] rounded px-2.5 py-0.5"
          >
            {row.attempts}
          </Badge>
        ),
      },
    ],
    [],
  );

  const handleExport = () => {
    toast({
      title: "Export Triggered",
      description: "Exporting failed records log to CSV...",
    });
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {/* Red Alert Banner */}
      <div className="p-4 border border-red-100 bg-red-50/50 rounded-2xl flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
        <div className="space-y-1">
          <h4 className="text-sm font-bold text-red-850">
            {totalItems} failed records pending review
          </h4>
          <p className="text-xs text-red-650 font-semibold">
            Records have been queued in the dead-letter queue. Retry
            individually or in bulk.
          </p>
        </div>
      </div>

      {/* Custom Bulk Action & Export Toolbar */}
      <div className="flex items-center justify-end">
        {/* Right: Action Buttons */}
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={handleExport}>
            <Download className="w-4 h-4 text-gray-900" />
            <span>Export</span>
          </Button>
        </div>
      </div>

      {/* Table Section */}
      <GenericTable
        data={failedRecordsData}
        columns={columns}
        rowKey="id"
        onExport={handleExport}
        exportLoading={false}
        pagination={pagination}
        onPageChange={handlePageChange}
      />
    </div>
  );
};

export default DiagnosticsTab;
